import React, { useContext, useState } from "react";
import { makeStyles, Theme } from "@material-ui/core/styles";
import { tesaBlue, tesaDarkRedError } from "../../styles/colors";
import CustomAlert from "../../components/Alert/CustomAlert";
import ErrorSnackbarContext from "../../contexts/ErrorSnackbarContext";
import CurrencyConversionApi from "../../api/services/CurrencyConversionApi";
import { HttpErrorType } from "../../models/HttpErrorType";
import useEnv from "../../hooks/env";
import FileUpload from "./FileUpload";
import exchangeRateTemplate from "../../assets/files/template-exchange-rates.xlsx";

type ExchangeRateUploadError = {
  response: {
    data: {
      keys: string[];
      message: string;
    };
  };
};

const useStyles = makeStyles((theme: Theme) => ({
  form: {
    display: "inline",
    "&:invalid": {
      "& input": {
        borderBottomColor: `${tesaDarkRedError}`,
        color: `${tesaDarkRedError}`,
      },
    },
  },
  input: {
    border: 0,
    borderBottom: `1px solid ${tesaBlue}`,
    fontFamily: "Proxima Nova",
    height: "1rem",
    marginLeft: theme.spacing(1),
    outline: "none",
    width: "7ch",
  },
  marginBottom5: {
    marginBottom: theme.spacing(5),
  },
  marginBottom2: {
    marginBottom: theme.spacing(2),
  },
  subHeading: {
    display: "block",
    fontSize: "1rem",
    fontWeight: 400,
    lineHeight: "1.5rem",
    marginBottom: theme.spacing(1),
  },
}));

const ACCEPTED_FILES = {
  ".xls": "application/vnd.ms-excel",
  ".xlsx": "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
};
const CURRENCY_EXCHANGE_YEAR_MAX = 9999;
const CURRENCY_EXCHANGE_YEAR_MIN = 2010;
const MAX_FILE_SIZE_MB = 5;
const SUCCESS_MESSAGE = "Successfully updated exchange rates";

function shouldDisableExchangeRateUpload(currencyExchangeYear: number): boolean | undefined {
  return currencyExchangeYear < CURRENCY_EXCHANGE_YEAR_MIN || currencyExchangeYear > CURRENCY_EXCHANGE_YEAR_MAX;
}

function isExchangeRateUploadError(error: HttpErrorType | undefined): error is ExchangeRateUploadError {
  if (!error?.response || !error.response.data) {
    return false;
  }
  return Object.keys(error.response.data).includes("keys");
}

const ExchangeRateUpload = () => {
  const classes = useStyles();
  const [currencyExchangeYear, setCurrencyExchangeYear] = useState(new Date().getFullYear());
  const { handleHttpError, showErrorSnackbar } = useContext(ErrorSnackbarContext);
  useContext(ErrorSnackbarContext);
  const { api } = useEnv();
  const currencyConversionApi = new CurrencyConversionApi(api);

  const handleExchangeRateUploadError = (err: HttpErrorType | undefined): void => {
    if (isExchangeRateUploadError(err)) {
      const { message, keys } = err.response.data;
      showErrorSnackbar(`${message}: ${keys.join(", ")}`);
    } else {
      handleHttpError(err);
    }
  };

  const handleUpload = (
    payload: FormData,
    progressCallback?: (progressEvent: { loaded: number; total: number }) => void,
  ) => {
    return currencyConversionApi.uploadExchangeRates(currencyExchangeYear, payload, progressCallback);
  };

  return (
    <div className={classes.marginBottom5}>
      <div className={classes.subHeading}>Currency Exchange Rate Upload</div>
      <CustomAlert
        severity="info"
        className={classes.marginBottom2}
        text="
          Uploading a new currency exchange rate excel directly updates all PNS Potential Euro
          conversions for all test requests in the selected year.
      "
      />
      <div style={{ marginBottom: "0.5rem" }}>
        year of upload{" "}
        <form className={classes.form}>
          <input
            type="number"
            className={classes.input}
            defaultValue={currencyExchangeYear}
            onChange={(e) => {
              setCurrencyExchangeYear(parseInt(e.target.value, 10));
            }}
            max={CURRENCY_EXCHANGE_YEAR_MAX}
            min={CURRENCY_EXCHANGE_YEAR_MIN}
          />
        </form>
      </div>
      <FileUpload
        acceptedFiles={ACCEPTED_FILES}
        buttonLabel="Upload Exchange Rates"
        templateHref={exchangeRateTemplate}
        disablePrimary={shouldDisableExchangeRateUpload(currencyExchangeYear)}
        handleError={handleExchangeRateUploadError}
        maxFileSizeMB={MAX_FILE_SIZE_MB}
        successMessage={SUCCESS_MESSAGE}
        upload={handleUpload}
      />
    </div>
  );
};

export default ExchangeRateUpload;
